import { formatMoney, formatUsPhone, queryKeys } from '@mevaser/shared'
import {
  Badge,
  Button,
  EmptyState,
  humanizeStatus,
  Input,
  ORDER_STATUS_TONE,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@mevaser/ui'
import { useInfiniteQuery } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import type { ColumnDef } from '@tanstack/react-table'
import { Plus, Search, ShoppingCart } from 'lucide-react'
import { useMemo, useState } from 'react'
import { Page, PageHeader } from '../components/app-shell.js'
import { DataTable } from '../components/data-table.js'
import { api } from '../lib/api.js'
import { useAuth } from '../lib/auth.js'

interface OrderRow {
  id: string
  orderNumber: string
  status: string
  channel: string
  locationName: string | null
  fulfillmentMethod: string
  customerName: string | null
  customerPhone: string | null
  totalCents: number
  paidCents: number
  balanceDueCents: number
  itemCount: number
  /** First line's product name (t1336). */
  firstItemName: string | null
  /** Lines still flagged as possibly the wrong unit (t1137). */
  openIdentityFlags: number
  createdAt: string
}

const FULFILLMENT_LABEL: Record<string, string> = {
  pickup: 'Pickup',
  shipping: 'Shipping',
  none: 'Plan only',
}

function paymentBadge(row: OrderRow): {
  label: string
  tone: 'neutral' | 'success' | 'warning' | 'danger'
} {
  if (row.status === 'refunded') return { label: 'Refunded', tone: 'danger' }
  if (row.balanceDueCents <= 0 && row.paidCents > 0) return { label: 'Paid', tone: 'success' }
  if (row.paidCents > 0) return { label: 'Partial', tone: 'warning' }
  return { label: 'Unpaid', tone: 'neutral' }
}

const DEFAULT_STATUS = 'except_cancelled'

export function OrdersPage() {
  const navigate = useNavigate()
  const { can } = useAuth()
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState(DEFAULT_STATUS)
  const [unverifiedOnly, setUnverifiedOnly] = useState(false)

  const filters = {
    q: search || undefined,
    status: status === 'all' ? undefined : status,
    identityFlag: unverifiedOnly ? ('open' as const) : undefined,
    pageSize: 50,
  }

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: queryKeys.orders.list(filters),
    queryFn: ({ pageParam }) =>
      api.get<{ items: OrderRow[]; total: number; page: number; pageSize: number }>('/orders', {
        query: { ...filters, page: pageParam },
      }),
    initialPageParam: 1,
    getNextPageParam: (last) =>
      last.page * last.pageSize < last.total ? last.page + 1 : undefined,
  })
  const rows = data?.pages.flatMap((p) => p.items) ?? []
  const total = data?.pages[0]?.total ?? 0

  const columns = useMemo<ColumnDef<OrderRow, unknown>[]>(
    () => [
      {
        header: 'Order',
        accessorKey: 'orderNumber',
        cell: ({ row }) => (
          <span className="font-identifier font-medium">{row.original.orderNumber}</span>
        ),
      },
      {
        header: 'Customer',
        accessorKey: 'customerName',
        cell: ({ row }) => (
          <div className="min-w-0">
            <p className="truncate">{row.original.customerName ?? 'Walk-in'}</p>
            {row.original.customerPhone ? (
              <p className="font-identifier text-xs text-foreground-muted">
                {formatUsPhone(row.original.customerPhone)}
              </p>
            ) : null}
          </div>
        ),
      },
      {
        header: 'Location',
        accessorKey: 'locationName',
        cell: ({ row }) => (
          <div className="min-w-0">
            <p className="text-foreground-muted">
              {row.original.locationName ?? (row.original.channel === 'web' ? 'Online' : '—')}
            </p>
            <p className="text-xs text-foreground-subtle">
              {FULFILLMENT_LABEL[row.original.fulfillmentMethod] ?? row.original.fulfillmentMethod}
            </p>
          </div>
        ),
      },
      {
        header: 'Status',
        accessorKey: 'status',
        cell: ({ row }) => (
          <Badge tone={ORDER_STATUS_TONE[row.original.status] ?? 'neutral'}>
            {humanizeStatus(row.original.status)}
          </Badge>
        ),
      },
      {
        header: 'Payment',
        accessorKey: 'paidCents',
        cell: ({ row }) => {
          const { label, tone } = paymentBadge(row.original)
          return <Badge tone={tone}>{label}</Badge>
        },
      },
      {
        header: 'Products',
        accessorKey: 'firstItemName',
        cell: ({ row }) => (
          <div className="flex max-w-64 items-center gap-1.5">
            <p className="min-w-0 truncate">{row.original.firstItemName ?? '—'}</p>
            {row.original.itemCount > 1 ? (
              <span className="shrink-0 text-xs text-foreground-muted">
                +{row.original.itemCount - 1} more
              </span>
            ) : null}
            {row.original.openIdentityFlags > 0 ? <Badge tone="warning">Unverified</Badge> : null}
          </div>
        ),
      },
      {
        header: 'Total',
        accessorKey: 'totalCents',
        cell: ({ row }) => (
          <div className="text-end">
            <p className="tabular font-medium">{formatMoney(row.original.totalCents)}</p>
            {row.original.balanceDueCents > 0 ? (
              <p className="text-xs text-warning tabular">
                {formatMoney(row.original.balanceDueCents)} due
              </p>
            ) : null}
          </div>
        ),
      },
      {
        header: 'Placed',
        accessorKey: 'createdAt',
        cell: ({ row }) => (
          <span className="whitespace-nowrap text-xs text-foreground-muted">
            {new Date(row.original.createdAt).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
            })}
          </span>
        ),
      },
    ],
    [],
  )

  return (
    <Page>
      <PageHeader
        title="Orders"
        description="Every sale, whether it came from the website, the counter or a kiosk."
        actions={
          can('orders:write') ? (
            <Button variant="primary" onClick={() => void navigate({ to: '/orders/new' })}>
              <Plus /> New sale
            </Button>
          ) : null
        }
      />

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="relative max-w-xs flex-1">
          <Search
            className="pointer-events-none absolute start-2.5 top-1/2 size-3.5 -translate-y-1/2 text-foreground-subtle"
            aria-hidden="true"
          />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Order number or phone…"
            className="ps-8"
          />
        </div>
        <Select value={status} onValueChange={(value) => setStatus(value)}>
          <SelectTrigger className="w-44">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="except_cancelled">All except cancelled</SelectItem>
            <SelectItem value="pending_payment">Awaiting payment</SelectItem>
            <SelectItem value="paid">Paid</SelectItem>
            <SelectItem value="fulfilling">Being prepared</SelectItem>
            <SelectItem value="shipped">Shipped</SelectItem>
            <SelectItem value="ready_for_pickup">Ready for pickup</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="refunded">Refunded</SelectItem>
          </SelectContent>
        </Select>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            className="size-3.5 accent-[var(--primary)]"
            checked={unverifiedOnly}
            onChange={(e) => setUnverifiedOnly(e.target.checked)}
          />
          Unverified units only
        </label>
      </div>

      <DataTable
        data={rows}
        columns={columns}
        isLoading={isLoading}
        onRowClick={(row) => void navigate({ to: '/orders/$orderId', params: { orderId: row.id } })}
        infinite={{
          total,
          hasMore: Boolean(hasNextPage),
          isFetchingMore: isFetchingNextPage,
          onLoadMore: () => void fetchNextPage(),
        }}
        empty={
          search || status !== DEFAULT_STATUS || unverifiedOnly ? (
            <EmptyState
              icon={Search}
              variant="no-results"
              title="No orders match"
              description="Try a different search or status."
              onClearFilters={() => {
                setSearch('')
                setStatus(DEFAULT_STATUS)
              }}
            />
          ) : (
            <EmptyState
              icon={ShoppingCart}
              title="No orders yet"
              description="Sales from every channel will appear here."
            />
          )
        }
      />
    </Page>
  )
}
